import React from 'react'
import { normalizeResolvedVideoCallQuality } from '../../../routing/videoCallQualityContract'

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function pickDefined(source, keys) {
  return keys.reduce((result, key) => {
    if (source[key] !== undefined) {
      result[key] = source[key]
    }

    return result
  }, {})
}

function normalizeBoolean(value, fallbackValue) {
  return typeof value === 'boolean' ? value : fallbackValue
}

function mergePlainObject(baseValue, nextValue) {
  if (!isPlainObject(nextValue)) {
    return baseValue
  }

  return {
    ...baseValue,
    ...nextValue,
  }
}

export const ZOOM_RTC_LAYOUTS = Object.freeze({
  grid: 'grid',
  pin: 'pin',
})

export const ZOOM_RTC_ADAPTER_CAPABILITIES = Object.freeze({
  screenshare: true,
  remoteMute: false,
  // Audit observers join as attendee-role (role_type 0) with AV muted.
  auditObserverJoin: true,
  playbackDeviceSelection: true,
  deviceHotSwap: true,
  customVideoPlaceholder: true,
  usernameOverlay: true,
  rtmBackedPresence: false,
})

export function createZoomRtcAdapterCapabilities(overrides = {}) {
  return {
    ...ZOOM_RTC_ADAPTER_CAPABILITIES,
    ...(isPlainObject(overrides) ? overrides : {}),
  }
}

export function normalizeZoomRtcLayout(layout, fallbackValue = ZOOM_RTC_LAYOUTS.pin) {
  if (layout === 0 || layout === '0') {
    return ZOOM_RTC_LAYOUTS.grid
  }

  if (layout === 1 || layout === '1') {
    return ZOOM_RTC_LAYOUTS.pin
  }

  if (typeof layout !== 'string') {
    return fallbackValue
  }

  const normalizedLayout = layout.trim().toLowerCase()
  if (normalizedLayout === 'grid') {
    return ZOOM_RTC_LAYOUTS.grid
  }

  if (normalizedLayout === 'pin' || normalizedLayout === 'pinned') {
    return ZOOM_RTC_LAYOUTS.pin
  }

  return fallbackValue
}

export function createZoomRtcCompatibilityProps({
  rtcProps = {},
  styleProps = {},
  callbacks = {},
} = {}) {
  const safeRtcProps = isPlainObject(rtcProps) ? rtcProps : {}

  return {
    rtcProps: {
      ...pickDefined(safeRtcProps, [
        'sessionName',
        'sessionPassword',
        'token',
        'userName',
        'userId',
        'participantMetadata',
        'cameraDeviceId',
        'microphoneDeviceId',
        'playbackDeviceId',
        'language',
        'dependentAssets',
        'webEndpoint',
        'sessionIdleTimeoutMins',
        'CustomVideoPlaceholder',
      ]),
      enableAudio: normalizeBoolean(safeRtcProps.enableAudio, true),
      enableVideo: normalizeBoolean(safeRtcProps.enableVideo, true),
      enableScreensharing: normalizeBoolean(safeRtcProps.enableScreensharing, false),
      layout: normalizeZoomRtcLayout(safeRtcProps.layout),
      videoQuality: normalizeResolvedVideoCallQuality(safeRtcProps.videoQuality),
    },
    styleProps: isPlainObject(styleProps) ? styleProps : {},
    callbacks: isPlainObject(callbacks) ? callbacks : {},
  }
}

export function mergeZoomRtcCompatibilityProps(baseProps = {}, nextProps = {}) {
  return createZoomRtcCompatibilityProps({
    ...baseProps,
    ...nextProps,
    rtcProps: mergePlainObject(baseProps?.rtcProps || {}, nextProps?.rtcProps),
    styleProps: mergePlainObject(baseProps?.styleProps || {}, nextProps?.styleProps),
    callbacks: mergePlainObject(baseProps?.callbacks || {}, nextProps?.callbacks),
  })
}

let zoomRtcModulePromise = null

export function loadZoomRtcModule() {
  if (!zoomRtcModulePromise) {
    zoomRtcModulePromise = import('./ZoomRtcCompatibilityShell').then((module) => ({
      ZoomRtcCompatibilityShell: module.ZoomRtcCompatibilityShell || module.default,
    }))
  }

  return zoomRtcModulePromise
}

export function preloadZoomRtcModule() {
  return loadZoomRtcModule().then(() => undefined)
}

const LazyZoomRtcCompatibilityShell = React.lazy(async () => {
  const module = await loadZoomRtcModule()

  return {
    default: module.ZoomRtcCompatibilityShell,
  }
})

export function ZoomRtcCompatibilitySurface({ fallback = null, ...props }) {
  return React.createElement(
    React.Suspense,
    { fallback },
    React.createElement(LazyZoomRtcCompatibilityShell, {
      fallback,
      ...props,
    }),
  )
}

ZoomRtcCompatibilitySurface.displayName = 'ZoomRtcCompatibilitySurface'
